import { createSlice, createSelector } from '@reduxjs/toolkit';
import { CourierUser } from '../shared/sdk-v1';
import { RootState } from './rootReducer';
import { hasAnyWarehouseApiEnabled } from '../utils/warehouse';
import { hasAnyVendorApiKey } from '../utils/courierSecrets';

interface PagePermissions {
  permissions: Record<string, boolean>;
  hasCustomPermissions: boolean;
}

interface AuthState {
  isAuthenticated: boolean;
  user?: CourierUser;
  token?: string;
  pagePermissions?: PagePermissions;
}

const initialState: AuthState = {
  isAuthenticated: false,
  user: undefined,
  token: undefined,
  pagePermissions: undefined
};

// localStorage.setItem coerces non-strings to text, so a stored `undefined`
// becomes the literal string "undefined" — which is truthy but not valid JSON.
// Guard against that (and any other corrupt value) so init() never throws.
const safeParse = <T>(raw: string | null): T | undefined => {
  if (!raw || raw === 'undefined' || raw === 'null') return undefined;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return undefined;
  }
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    init: (state) => {
      const token = localStorage.getItem('accessToken');
      const parsedUser = safeParse<CourierUser>(localStorage.getItem('user'));
      const parsedRoles = safeParse<string[]>(localStorage.getItem('roles'));
      const tokenExpiration = localStorage.getItem('tokenExpiration');
      const parsedPagePermissions = safeParse<PagePermissions>(
        localStorage.getItem('pagePermissions')
      );
      if (
        token &&
        token !== 'undefined' &&
        parsedUser &&
        parsedRoles &&
        tokenExpiration &&
        new Date(tokenExpiration) > new Date()
      ) {
        state.isAuthenticated = true;
        state.token = token;
        state.user = parsedUser;
        state.user.roles = parsedRoles;
        if (parsedPagePermissions) {
          state.pagePermissions = parsedPagePermissions;
        }
      }
      // Otherwise stay logged out, but DON'T delete storage here: init() re-runs
      // right after login(), and a login response can legitimately lack `user`.
      // Wiping the still-valid token would make the next request unauthenticated
      // → 401 → forced logout. safeParse() already prevents crashes on bad data.
    },
    login: (state, action) => {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      state.isAuthenticated = true;
      state.token = action.payload.token;
      state.user = action.payload.user;
      localStorage.setItem('accessToken', JSON.stringify(action.payload));
      // Never persist an undefined user — JSON.stringify(undefined) is the JS
      // value undefined, which setItem coerces to the string "undefined" and
      // later breaks JSON.parse in init().
      if (action.payload.user) {
        localStorage.setItem('user', JSON.stringify(action.payload.user));
      } else {
        localStorage.removeItem('user');
      }
      // set the token expiration date by calculating the ttl + current time
      const tokenExpiration = new Date(Date.now() + action.payload.ttl * 1000);
      localStorage.setItem('tokenExpiration', tokenExpiration.toISOString());
    },
    logout: (state) => {
      state.isAuthenticated = false;
      state.token = undefined;
      state.user = undefined;
      state.pagePermissions = undefined;
      localStorage.removeItem('accessToken');
      localStorage.removeItem('tokenExpiration');
      localStorage.removeItem('user');
      localStorage.removeItem('roles');
      localStorage.removeItem('pagePermissions');
    },
    updateUser: (state, action) => {
      state.user = action.payload;
      localStorage.setItem('user', JSON.stringify(action.payload));
    },
    setUserRoles: (state, action) => {
      if (state.user) {
        state.user.roles = action.payload;
        localStorage.setItem('roles', JSON.stringify(action.payload));
      }
    },
    setPagePermissions: (state, action) => {
      state.pagePermissions = action.payload;
      if (action.payload) {
        localStorage.setItem('pagePermissions', JSON.stringify(action.payload));
      } else {
        localStorage.removeItem('pagePermissions');
      }
    }
  },
  selectors: {
    getIsAuthenticated: (state: AuthState) => state.isAuthenticated,
    getUser: (state: AuthState) => state.user,
    getUserId: (state: AuthState) => state.user?.id,
    getToken: (state: AuthState) => state.token,
    getPagePermissions: (state: AuthState) => state.pagePermissions
  }
});

export const { init, login, logout, updateUser, setUserRoles, setPagePermissions } = authSlice.actions;

export const { getIsAuthenticated, getToken, getUserId, getUser, getPagePermissions } = authSlice.selectors;

// Custom selector that depends on courier settings
export const getHasApiKey = (state: RootState) => {
  const settings = state.courier.courier?.courierSetting;
  return !!(hasAnyVendorApiKey(settings) ||
           settings?.isLogisApiEnabled) ||
           hasAnyWarehouseApiEnabled(settings);
};

export default authSlice.reducer;
